import React, { FC, ReactNode, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { Nullable } from '@rbi-ctg/frontend';
import ConfirmDialog, { IConfirmProps } from 'components/confirm-dialog';
import Dialog from 'components/dialog';
import { IDialogProps } from 'components/dialog/types';
import { splashScreenHiddenDeferred } from 'utils/hide-splash-screen';
import noop from 'utils/noop';

export type DialogCb<T> = (data?: Nullable<T>) => void;

interface IUseDialogBaseArgs<T> {
  onConfirm?: DialogCb<T>;
  onCancel?: DialogCb<T>;
  onDismiss?: DialogCb<T>;
  onOpen?: DialogCb<T>;
  type?: string;
  showCancel?: boolean;
  init?: boolean;
  allowDismiss?: boolean;
  allowClose?: boolean;
  showCloseButton?: boolean;
  isErrorModal?: boolean;
}

type UseDialogComponentRequired<P> = {
  Component: React.ComponentType<React.PropsWithChildren<Props<P>>>;
  modalAppearanceEventMessage?: string;
};
type UseDialogMessageRequired<P> = {
  Component?: React.ComponentType<React.PropsWithChildren<Props<P>>>;
  modalAppearanceEventMessage: string;
};

type UseDialogArgs<T, P = {}> = IUseDialogBaseArgs<T> &
  (UseDialogComponentRequired<P> | UseDialogMessageRequired<P>);

export interface IUseDialogProps {
  heading?: string;
  body?: ReactNode;
  bodyComponent?: ReactNode;
  buttonLabel?: string;
  image?: ReactNode;
}

export interface IUseDialogComponentProps {
  onDismiss: () => void;
  onConfirm: () => void;
}

export type Props<P> = IUseDialogProps &
  Partial<IDialogProps> &
  Partial<IConfirmProps> &
  Omit<P, keyof IUseDialogComponentProps>;

export type UseDialogHook<T, P = {}> = [
  FC<React.PropsWithChildren<Props<P>>>,
  DialogCb<T>,
  Nullable<T>,
  () => void
];

/**
 * a hook + HOC that returns a modal ready to use
 * @deprecated use src/components/dialog/index.tsx or src/components/confirm-dialog/index.tsx if possible for better performance and a more straight-forward interface.
 * They are nearly the same otherwise.
 * Example:
 *  const [showModal, setShowModal] = useState(false)
 *  ...
 *  {showModal && <Dialog...}
 */
export default function useDialogModal<T extends object, P = {}>({
  Component,
  onConfirm = noop,
  onCancel = noop,
  onDismiss = onCancel,
  onOpen = noop,
  showCancel = false,
  init = false,
  allowDismiss = true,
  allowClose = true,
  /**
   * A small string sent to mParticle describing the purpose of the modal.
   */
  modalAppearanceEventMessage,
  isErrorModal,
}: UseDialogArgs<T, P>): UseDialogHook<T, P> {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(init);
  const [pendingData, setPending] = useState<Nullable<T>>(null);
  const openDialog = useCallback(
    (data?: Nullable<T>) => {
      // there is a bug on iOS if you display a modal before splash screen is gone you can't get off of the splash screen
      splashScreenHiddenDeferred.promise.then(() => {
        onOpen(data);
        setPending(data ?? null);
        setOpen(true);
      });
    },
    [onOpen]
  );
  const cancelDialog = useCallback(() => {
    onCancel(pendingData);
    setPending(null);
    if (allowClose) {
      setOpen(false);
    }
  }, [allowClose, onCancel, pendingData]);
  const dismissDialog = useCallback(() => {
    onDismiss(pendingData);
    setPending(null);
    if (allowDismiss && allowClose) {
      setOpen(false);
    }
  }, [allowClose, allowDismiss, onDismiss, pendingData]);
  const confirmDialog = useCallback(() => {
    onConfirm(pendingData);
    setPending(null);
    if (allowClose) {
      setOpen(false);
    }
  }, [allowClose, onConfirm, pendingData]);

  const DialogComponent: FC<React.PropsWithChildren<Props<P>>> = useCallback(
    ({
      body = formatMessage({ id: 'errorProcessingRequest' }),
      buttonLabel = formatMessage({ id: 'okay' }),
      heading = formatMessage({ id: 'somethingWrong' }),
      image,
      showCloseButton,
      /**
       * The DialogComponent can be rendered using different props
       * than the `useDialogComponent` hook is called with.
       */
      modalAppearanceEventMessage: modalAppearanceEventMessageOverride,
      ...rest
    }) => {
      if (!open) {
        return null;
      }
      // FIXME: update type to avoid make this
      const message: string =
        pendingData && 'message' in pendingData ? (pendingData.message as string) : '';
      const title: string =
        pendingData && 'title' in pendingData ? (pendingData.title as string) : '';

      if (Component) {
        return (
          <Component
            onDismiss={dismissDialog}
            onConfirm={confirmDialog}
            modalAppearanceEventMessage={modalAppearanceEventMessageOverride}
            heading={title! || heading}
            {...(rest as P)}
          />
        );
      }

      return showCancel ? (
        // @ts-ignore TS(2590) FIXME: Expression produces a union type that is too complex to represent.
        <ConfirmDialog
          showDialog={open}
          heading={title! || heading}
          body={message || body}
          image={image}
          confirmLabel={buttonLabel}
          onConfirm={confirmDialog}
          onCancel={cancelDialog}
          onDismiss={dismissDialog}
          modalAppearanceEventMessage={
            modalAppearanceEventMessageOverride || modalAppearanceEventMessage!
          }
          isErrorModal={isErrorModal}
          {...rest}
        />
      ) : (
        <Dialog
          showDialog={open}
          heading={title! || heading}
          body={message || body}
          image={image}
          onDismiss={dismissDialog}
          showCloseButton={showCloseButton}
          confirmButtonLabel={buttonLabel}
          confirmDialog={confirmDialog}
          modalAppearanceEventMessage={
            modalAppearanceEventMessageOverride || modalAppearanceEventMessage!
          }
          isErrorModal={isErrorModal}
          {...rest}
        />
      );
    },
    // @ts-ignore TODO: represents a union that is too complex to express
    [
      formatMessage,
      open,
      pendingData,
      Component,
      showCancel,
      confirmDialog,
      cancelDialog,
      dismissDialog,
      modalAppearanceEventMessage,
      isErrorModal,
    ]
  );

  return [DialogComponent, openDialog, pendingData, dismissDialog];
}
